import type { TrackingConfig, EventTracker, TrackingPausedReason } from './types';
import { loadContext, saveContext, clearContext, type PersistedContext } from './persistence';

// Constants for queue management
const MAX_QUEUE_SIZE = 1000;
const PENDING_EVENTS_KEY = 'firstdistro_pending_events';
const MAX_PENDING_EVENTS = 500;

/**
 * Event context captured from browser
 */
interface EventContext {
  url: string;
  pathname: string;
  referrer?: string;
  userAgent: string;
  screenWidth: number;
  screenHeight: number;
  timestamp: string;
}

/**
 * Event payload structure
 */
interface Event {
  eventName: string;
  properties: object;
  timestamp: string;
  sessionId: string;
  userId: string | null;
  accountId: string | null;
  context: EventContext;
}

/**
 * Batch payload structure
 */
interface BatchPayload {
  events: Event[];
  vendorId: string;
  batchId: string;
  apiKey?: string;  // Included for sendBeacon (can't set headers)
}

/**
 * EventTracker class
 * 
 * Handles batched event tracking with auto-flush, retry logic, and page unload handling.
 */
export class EventTrackerImpl implements EventTracker {
  private queue: Event[] = [];
  private batchSize: number;
  private flushInterval: number;
  private sampleRate: number;
  private vendorId: string;
  private apiKey: string;
  private apiUrl: string;
  private sessionId: string;
  private userId: string | null = null;
  private accountId: string | null = null;
  private flushTimer: number | null = null;
  private beforeUnloadHandler: (() => void) | null = null;
  private debug: boolean = false;

  // Pause state for billing/trial expiration
  private isPaused: boolean = false;
  private pausedReason: TrackingPausedReason | null = null;
  private pausedUntil: number = 0; // timestamp when to retry
  private onTrackingPaused?: (reason: TrackingPausedReason) => void;

  constructor(options: {
    config: TrackingConfig;
    vendorId: string;
    apiKey: string;
    apiUrl: string;
    debug?: boolean;
    onTrackingPaused?: (reason: TrackingPausedReason) => void;
  }) {
    const { config, vendorId, apiKey, apiUrl, debug = false, onTrackingPaused } = options;

    // Validate required parameters
    if (!vendorId || !apiKey || !apiUrl) {
      throw new Error('EventTracker: vendorId, apiKey, and apiUrl are required');
    }

    this.vendorId = vendorId;
    this.apiKey = apiKey;
    this.apiUrl = apiUrl;
    this.debug = debug;
    this.onTrackingPaused = onTrackingPaused;

    // Set configuration with defaults
    this.batchSize = config.batchSize ?? 20;
    this.flushInterval = config.flushInterval ?? 5000;
    this.sampleRate = config.sampleRate ?? 1.0;

    // Generate or retrieve session ID
    this.sessionId = this.getOrCreateSessionId();

    // Load persisted user/account data
    this.loadPersistedData();

    // Load any events that were saved during a previous 402 (payment required) response
    this.loadPendingEvents();

    // Start auto-flush timer
    this.startAutoFlush();

    // Set up page unload handler
    this.setupBeforeUnload();

    if (this.debug) {
      console.log('[EventTracker] Initialized', {
        batchSize: this.batchSize,
        flushInterval: this.flushInterval,
        sampleRate: this.sampleRate,
        sessionId: this.sessionId
      });
    }
  }

  /**
   * Get or create session ID
   * Persists across page reloads using sessionStorage
   */
  private getOrCreateSessionId(): string {
    try {
      const stored = sessionStorage.getItem('firstdistro_session_id');
      if (stored) {
        return stored;
      }

      const sessionId = `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
      sessionStorage.setItem('firstdistro_session_id', sessionId);
      return sessionId;
    } catch (error) {
      // Private browsing mode or storage disabled
      return `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    }
  }

  /**
   * Load persisted user and account data from localStorage
   */
  private loadPersistedData(): void {
    try {
      const context = loadContext();
      if (context) {
        if (context.userId) {
          this.userId = context.userId;
      }
        if (context.accountId) {
          this.accountId = context.accountId;
        }
      }
    } catch (error) {
      // Ignore storage errors
    }
  }

  /**
   * Get browser context for events
   */
  private getContext(): EventContext {
    return {
      url: window.location.href,
      pathname: window.location.pathname,
      referrer: document.referrer || undefined,
      userAgent: navigator.userAgent,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      timestamp: new Date().toISOString()
    };
  }

  /**
   * Track an event
   */
  track(eventName: string, properties?: object): void {
    try {
      // Check if tracking is paused (e.g., trial expired)
      if (this.isPaused && Date.now() < this.pausedUntil) {
        if (this.debug) {
          console.log('[EventTracker] Tracking paused, event dropped:', eventName);
        }
        return; // Silently drop events while paused
      }

      // If pause period expired, reset pause state and try again
      if (this.isPaused && Date.now() >= this.pausedUntil) {
        this.isPaused = false;
        this.pausedReason = null;
        if (this.debug) {
          console.log('[EventTracker] Pause period expired, resuming tracking');
        }
      }

      // Validate inputs
      if (!eventName || typeof eventName !== 'string' || eventName.trim().length === 0) {
        console.warn('[EventTracker] Invalid eventName:', eventName);
        return;
      }

      if (properties !== undefined && (typeof properties !== 'object' || properties === null || Array.isArray(properties))) {
        console.warn('[EventTracker] properties must be an object:', properties);
        return;
      }

      // Warn if tracking events without account context (except system events)
      // This helps vendors debug issues where events can't be linked to accounts
      if (!this.accountId && !['$pageview', '$setup'].includes(eventName)) {
        console.warn(
          `[FirstDistro] Event "${eventName}" tracked without account context. ` +
          'Call setup({ account: { id: ... } }) first for accurate customer tracking. ' +
          'Events without account_id cannot be linked to customers in the dashboard.'
        );
      }

      // Apply sample rate
      if (Math.random() > this.sampleRate) {
        if (this.debug) {
          console.log('[EventTracker] Event sampled out:', eventName);
        }
        return;
      }

      // Create event object
      const event: Event = {
        eventName: eventName.trim(),
        properties: properties || {},
        timestamp: new Date().toISOString(),
        sessionId: this.sessionId,
        userId: this.userId,
        accountId: this.accountId,
        context: this.getContext()
      };

      // $pageview properties already carry the scrubbed url/referrer (query +
      // fragment stripped, maskUrl applied). getContext() re-reads the raw
      // window.location.href / document.referrer, which would re-introduce that
      // PII into context. Mirror the sanitized values onto the context.
      if (event.eventName === '$pageview') {
        const pageProps = event.properties as { url?: unknown; referrer?: unknown };
        if (typeof pageProps.url === 'string') {
          event.context.url = pageProps.url;
        }
        event.context.referrer =
          typeof pageProps.referrer === 'string' ? pageProps.referrer : undefined;
      }

      // Enforce max queue size - drop oldest if full
      if (this.queue.length >= MAX_QUEUE_SIZE) {
        const dropped = this.queue.shift();
        if (this.debug) {
          console.warn('[EventTracker] Queue full, dropped oldest event:', dropped?.eventName);
        }
      }

      // Add to queue
      this.queue.push(event);

      if (this.debug) {
        console.log('[EventTracker] Event queued:', eventName, 'Queue size:', this.queue.length);
      }

      // Auto-flush if batch size reached
      if (this.queue.length >= this.batchSize) {
        this.flush();
      }
    } catch (error) {
      console.warn('[EventTracker] Error tracking event:', error);
    }
  }

  /**
   * Unified identity setup. Sets user/account context silently and emits a
   * single `$setup` event (no legacy `$identify`/`$group` pair).
   *
   * Identity (userId/accountId) is carried only on the event columns; event
   * properties hold display + business traits using Option A prefixed keys
   * (userName/userEmail/accountName) with other account traits unprefixed.
   */
  setup(params: {
    userId?: string;
    userTraits?: object;
    accountId?: string;
    accountTraits?: object;
  }): void {
    try {
      const userId =
        typeof params.userId === 'string' ? params.userId.trim() : '';
      const accountId =
        typeof params.accountId === 'string' ? params.accountId.trim() : '';

      // Set account context first (correct ordering), then user — silently.
      if (accountId) {
        const ACCOUNT_ID_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9._-]{0,254}$/;
        if (!ACCOUNT_ID_PATTERN.test(accountId)) {
          console.warn(
            `[EventTracker] accountId "${accountId}" contains invalid characters. ` +
            'Account IDs must start with a letter or number and contain only letters, numbers, dots, dashes, or underscores (1-255 chars). ' +
            'The server will reject events with this accountId.'
          );
        }
        this.accountId = accountId;
      }
      if (userId) {
        this.userId = userId;
      }

      // Nothing to identify — skip emission.
      if (!userId && !accountId) {
        return;
      }

      const userTraits = (params.userTraits || {}) as Record<string, unknown>;
      const accountTraits = (params.accountTraits || {}) as Record<string, unknown>;

      const properties: Record<string, unknown> = {};

      // User business traits go down first; the account owns the unprefixed
      // namespace, so a colliding key (e.g. `plan`) deterministically resolves
      // to the account value below. name/email are prefixed to userName/userEmail.
      const { name: userName, email: userEmail, ...userRest } = userTraits;
      Object.assign(properties, userRest);

      // Account traits own the unprefixed namespace; assigned last so they win
      // on collision. name -> accountName to avoid clobbering the user's display name.
      const { name: accountName, ...accountRest } = accountTraits;
      Object.assign(properties, accountRest);

      if (accountName !== undefined) {
        properties.accountName = accountName;
      }
      if (userName !== undefined) {
        properties.userName = userName;
      }
      if (userEmail !== undefined) {
        properties.userEmail = userEmail;
      }

      this.track('$setup', properties);

      if (this.debug) {
        console.log('[EventTracker] Setup:', { userId: this.userId, accountId: this.accountId });
      }
    } catch (error) {
      console.warn('[EventTracker] Error during setup:', error);
    }
  }

  /**
   * Flush queued events to API
   * Chunks events into batches of MAX_BATCH_SIZE (100) to avoid server rejection
   */
  flush(sync: boolean = false): void {
    try {
      // Early return if queue is empty
      if (this.queue.length === 0) {
        return;
      }

      // Server rejects batches > 100 events, so we chunk
      const MAX_BATCH_SIZE = 100;
      const events = this.queue.splice(0, MAX_BATCH_SIZE);
      const batchId = `batch_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

      const payload: BatchPayload = {
        events,
        vendorId: this.vendorId,
        batchId
      };

      if (this.debug) {
        console.log('[EventTracker] Flushing batch:', batchId, 'Events:', events.length, 'Remaining:', this.queue.length);
      }

      if (sync) {
        // Synchronous flush using sendBeacon (for page unload)
        // For sync mode, send all remaining batches too
        this.flushWithBeacon(payload);
        while (this.queue.length > 0) {
          const moreEvents = this.queue.splice(0, MAX_BATCH_SIZE);
          const moreBatchId = `batch_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
          this.flushWithBeacon({ events: moreEvents, vendorId: this.vendorId, batchId: moreBatchId });
        }
      } else {
        // Asynchronous flush using fetch
        this.flushWithFetch(payload, events);

        // If more events remain, schedule another flush
        if (this.queue.length > 0) {
          setTimeout(() => this.flush(), 100);
        }
      }
    } catch (error) {
      console.warn('[EventTracker] Error flushing events:', error);
    }
  }

  /**
   * Flush using navigator.sendBeacon (synchronous, for page unload)
   * Note: sendBeacon cannot set custom headers, so we include API key in the payload body
   */
  private flushWithBeacon(payload: BatchPayload): void {
    try {
      // Include API key in payload since sendBeacon can't set headers
      const payloadWithAuth: BatchPayload = { ...payload, apiKey: this.apiKey };

      const blob = new Blob([JSON.stringify(payloadWithAuth)], { type: 'application/json' });
      const success = navigator.sendBeacon(
        `${this.apiUrl}/api/tracking/events/batch`,
        blob
      );

      if (!success) {
        console.warn('[EventTracker] sendBeacon failed, events may be lost');
      } else if (this.debug) {
        console.log('[EventTracker] Events sent via sendBeacon');
      }
    } catch (error) {
      console.warn('[EventTracker] sendBeacon error:', error);
    }
  }

  /**
   * Flush using fetch (asynchronous, with per-batch retry logic and exponential backoff)
   * @param retryCount - Current retry attempt (0 = first attempt)
   */
  private async flushWithFetch(payload: BatchPayload, events: Event[], retryCount: number = 0): Promise<void> {
    const batchId = payload.batchId;

    try {
      const response = await fetch(`${this.apiUrl}/api/tracking/events/batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': this.apiKey
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        // Check if error is retryable
        const isRetryable = response.status >= 500 || response.status === 429;
        const isAuthError = response.status === 401 || response.status === 403;
        const isPaymentRequired = response.status === 402;

        if (isPaymentRequired) {
          // Trial expired or subscription required - pause tracking
          try {
            const data = await response.json();
            this.handlePaymentRequired(data, events);
          } catch {
            this.handlePaymentRequired({}, events);
          }
          return; // Don't retry payment required errors
        }

        if (isAuthError) {
          // Don't retry auth errors
          console.warn('[EventTracker] Authentication error, events discarded');
          return;
        }

        if (isRetryable && retryCount < 3) {
          // Re-queue events immediately so beforeunload can flush them if user navigates away
          this.queue.unshift(...events);

          // Exponential backoff: 1s, 2s, 4s (capped at 5s)
          const delayMs = Math.min(1000 * Math.pow(2, retryCount), 5000);

          if (this.debug) {
            console.log(`[EventTracker] Request failed, retrying in ${delayMs}ms. Retry count:`, retryCount + 1);
          }

          // Schedule retry - flush will pick up events from queue
          setTimeout(() => this.flush(), delayMs);
          return;
        } else {
          // Max retries reached or non-retryable error
          console.warn('[EventTracker] Failed to send batch after retries:', batchId);
        }
      } else {
        // Success
        if (this.debug) {
          console.log('[EventTracker] Events sent successfully');
        }
      }
    } catch (error) {
      // Network error - retry if count allows
      if (retryCount < 3) {
        // Re-queue events immediately so beforeunload can flush them if user navigates away
        this.queue.unshift(...events);

        // Exponential backoff: 1s, 2s, 4s (capped at 5s)
        const delayMs = Math.min(1000 * Math.pow(2, retryCount), 5000);

        if (this.debug) {
          console.log(`[EventTracker] Network error, retrying in ${delayMs}ms. Retry count:`, retryCount + 1);
        }

        // Schedule retry - flush will pick up events from queue
        setTimeout(() => this.flush(), delayMs);
      } else {
        console.warn('[EventTracker] Network error after max retries for batch:', batchId);
      }
    }
  }

  /**
   * Handle 402 Payment Required response
   * Persists events to localStorage for later retry, pauses tracking, and fires callback
   */
  private handlePaymentRequired(data: any, currentBatchEvents: Event[] = []): void {
    const retryAfterSeconds = data.retry_after || 3600; // Default 1 hour

    this.isPaused = true;
    this.pausedUntil = Date.now() + retryAfterSeconds * 1000;
    this.pausedReason = {
      code: data.code || 'payment_required',
      message: data.message || 'Payment required to continue tracking',
      dashboardUrl: data.dashboard_url || '',
    };

    // Persist all pending events (current batch + queue) to localStorage before clearing
    const allPendingEvents = [...currentBatchEvents, ...this.queue];
    if (allPendingEvents.length > 0) {
      this.persistEventsForLater(allPendingEvents);
      if (this.debug) {
        console.log('[EventTracker] Persisted', allPendingEvents.length, 'events to localStorage for later');
      }
    }

    // Clear queue after persisting
    this.queue = [];

    if (this.debug) {
      console.log('[EventTracker] Tracking paused:', this.pausedReason);
    }

    // Fire callback if provided
    if (this.onTrackingPaused && this.pausedReason) {
      try {
        this.onTrackingPaused(this.pausedReason);
      } catch (error) {
        console.warn('[EventTracker] Error in onTrackingPaused callback:', error);
      }
    }
  }

  /**
   * Persist events to localStorage for later retry (e.g., after 402 response)
   */
  private persistEventsForLater(events: Event[]): void {
    if (typeof window === 'undefined') return;

    try {
      const existingJson = localStorage.getItem(PENDING_EVENTS_KEY);
      const existing: Event[] = existingJson ? JSON.parse(existingJson) : [];

      // Combine existing and new events, keeping only the most recent MAX_PENDING_EVENTS
      const combined = [...existing, ...events].slice(-MAX_PENDING_EVENTS);

      localStorage.setItem(PENDING_EVENTS_KEY, JSON.stringify(combined));
    } catch (error) {
      // Quota exceeded or private mode - events will be lost
      if (this.debug) {
        console.warn('[EventTracker] Failed to persist events to localStorage:', error);
      }
    }
  }

  /**
   * Load pending events from localStorage (saved during previous 402 responses)
   */
  private loadPendingEvents(): void {
    if (typeof window === 'undefined') return;

    try {
      const pendingJson = localStorage.getItem(PENDING_EVENTS_KEY);
      if (!pendingJson) return;

      const pendingEvents: Event[] = JSON.parse(pendingJson);
      if (pendingEvents.length > 0) {
        // Add pending events to the front of the queue
        this.queue.unshift(...pendingEvents);

        // Clear from localStorage since they're now in the queue
        localStorage.removeItem(PENDING_EVENTS_KEY);

        if (this.debug) {
          console.log('[EventTracker] Loaded', pendingEvents.length, 'pending events from localStorage');
        }
      }
    } catch (error) {
      // Invalid JSON or other error - clear the corrupted data
      localStorage.removeItem(PENDING_EVENTS_KEY);
      if (this.debug) {
        console.warn('[EventTracker] Failed to load pending events:', error);
      }
    }
  }

  /**
   * Start auto-flush timer
   */
  private startAutoFlush(): void {
    this.stopAutoFlush(); // Clear existing timer

    this.flushTimer = window.setInterval(() => {
      this.flush();
    }, this.flushInterval);

    if (this.debug) {
      console.log('[EventTracker] Auto-flush timer started:', this.flushInterval, 'ms');
    }
  }

  /**
   * Stop auto-flush timer
   */
  private stopAutoFlush(): void {
    if (this.flushTimer !== null) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  /**
   * Set up beforeunload handler for sync flush
   */
  private setupBeforeUnload(): void {
    this.beforeUnloadHandler = () => {
      this.flush(true); // Sync flush
    };
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  /**
   * Remove beforeunload handler
   */
  private removeBeforeUnload(): void {
    if (this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
  }

  /**
   * Destroy the EventTracker instance
   * Cleans up all resources and performs final flush
   */
  destroy(): void {
    try {
      // Stop auto-flush timer
      this.stopAutoFlush();

      // Remove beforeunload handler
      this.removeBeforeUnload();

      // Final sync flush
      this.flush(true);

      // Clear instance variables
      this.queue = [];
      this.userId = null;
      this.accountId = null;

      if (this.debug) {
        console.log('[EventTracker] Destroyed');
      }
    } catch (error) {
      console.warn('[EventTracker] Error during destroy:', error);
    }
  }
}

